import arraySort from 'array-sort';
import { ContentType, Publication } from '@amzn/awscat-pattern-library-service-model';
import { Box, Grid, Header, Input, Spinner } from '@amzn/awsui-components-core';
import React, { useEffect, useState, useRef } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { withAppContext } from '../../contexts/AppContext';
import '../../resources/styles/scss/components/pages/HomePage.css';
import { Filters } from './ContentFilters';
import NewPublicationFilters from './NewPublicationFilters';
import LatestUpdateBar from './LatestUpdateBar';
import { decodeSearchtextString } from '../utils/Search';
import {
  HomePageBanner,
  HomePageCtx,
  HomePageCtxData,
  ContentSortDropDown,
  ContentTypeSlicer,
  VisibilityTypeSlicer,
  PublicationCardViewList,
  EmptySearchResult,
} from './content-home-page';
import ReactRouterProps from '../utils/ReactRouterProps';
import { useReportPageLoadTime } from '../../monitoring/PageLoadTimeReporter';

const DUPLICATE_SEARCH_TIMER_DELAY = 2000;

const HomePage = (props: ReactRouterProps) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const duplicateSearchTimer = useRef(null);

  const [filteredPublications, setFilteredPublications] = useState<Publication[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [allPublications, setAllPublications] = useState<Publication[]>([]);
  const [posts, setPosts] = useState<Publication[]>([]);
  const [filterOptions, setFilterOptions] = useState<Filters>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    const fetchPublications = async () => {
      const searchInput = decodeSearchtextString(props?.location?.state?.searchInput || '');
      if (searchInput?.length) {
        setSearchTerm(searchInput);
      }
      const publicationLists = await mplServiceClient.listPublicationsThroughS3();
      const validPublications = publicationLists.publications.filter((publication) =>
        [ContentType.Pattern, ContentType.Guide, ContentType.Strategy].includes(
          publication.contentMetadata.contentType,
        ),
      );
      arraySort(validPublications, 'lastUpdated', { reverse: true });
      setAllPublications(validPublications);
      setPosts(
        publicationLists.publications.filter(
          (p) => p.contentMetadata.contentType === ContentType.Post,
        ),
      );
      if (searchInput?.length) {
        await loadSearchPublications(searchInput, validPublications);
      } else {
        setFilteredPublications(validPublications);
        setPublications(validPublications);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    fetchPublications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // @ts-expect-error TS7006 - strict migration
  const handleSearchChange = async (event) => {
    setSearchTerm(decodeSearchtextString(event.detail.value));
    // @ts-expect-error TS2769 - strict migration
    clearTimeout(duplicateSearchTimer.current);
    // @ts-expect-error TS2322 - strict migration
    duplicateSearchTimer.current = setTimeout(async () => {
      await loadSearchPublications(event.detail.value, allPublications);
    }, DUPLICATE_SEARCH_TIMER_DELAY);
  };

  const loadSearchPublications = async (searchInput: string, validPublications: Publication[]) => {
    setIsLoading(true);
    if (!searchInput.length) {
      setPublications(validPublications);
      setFilteredPublications(validPublications);
    } else {
      const { publications: searchPublications } =
        await mplServiceClient.searchPublications(searchInput);
      const searchPublicationsIdsSet = new Set(searchPublications.map((p) => p.contentId));
      const formatSearchPublications = validPublications.filter((p) =>
        searchPublicationsIdsSet.has(p.contentId),
      );
      setPublications(formatSearchPublications);
      setFilteredPublications(formatSearchPublications);
    }
    setIsLoading(false);
  };

  // @ts-expect-error TS7006 - strict migration
  const handleSearchEnterPress = async (event) => {
    // @ts-expect-error TS2769 - strict migration
    clearTimeout(duplicateSearchTimer.current);
    if (event.detail.keyCode === 13) {
      event.preventDefault();
      await loadSearchPublications(searchTerm, allPublications);
    }
  };

  const homepageContextData: HomePageCtxData = {
    filteredPublications,
    setFilteredPublications,
    filterOptions,
    setFilterOptions,
    allPublications,
    searchTerm,
  };

  // Count the page as loaded once the publication tiles show real data, not the spinner or the empty state.
  const isPublicationListShown = !isLoading && filteredPublications.length > 0;
  useReportPageLoadTime('home', isPublicationListShown);

  const renderPublications = () => {
    if (isLoading) {
      return <Spinner />;
    }
    if (!filteredPublications.length) {
      return <EmptySearchResult />;
    }
    return <PublicationCardViewList />;
  };

  const displayFilterCount = () => {
    if (filteredPublications.length === allPublications.length) {
      return allPublications.length;
    }
    return `${filteredPublications.length} of ${allPublications.length}`;
  };

  return (
    <HomePageCtx.Provider value={homepageContextData}>
      <HomePageBanner />
      <LatestUpdateBar allPosts={posts} loading={isLoading} />
      <Box padding={{ top: 'l', bottom: 's', horizontal: 'l' }}>
        <Grid disableGutters gridDefinition={[{ colspan: 12 }]}>
          <Grid gridDefinition={[{ colspan: { default: 2 } }, { colspan: { default: 10 } }]}>
            <NewPublicationFilters publications={publications} />
            <Box>
              <div>
                <Box
                  variant="div"
                  padding={{ bottom: 's' }}
                  className="home-page-content-view-header"
                >
                  <div className="home-page-content-title">
                    <Header variant="h3" counter={`(${displayFilterCount()})`}>
                      Content
                    </Header>
                  </div>
                  <div className="home-page-search-container">
                    <Input
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchEnterPress}
                      placeholder="Find content"
                      type="search"
                      ariaRequired
                    />
                  </div>
                </Box>
                <Box
                  variant="div"
                  padding={{ top: 'xl' }}
                  className="home-page-content-view-filter-bar"
                >
                  <div className="home-page-content-view-slicer-container">
                    <ContentTypeSlicer />
                    <VisibilityTypeSlicer />
                  </div>
                  <ContentSortDropDown />
                </Box>
              </div>
              {renderPublications()}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </HomePageCtx.Provider>
  );
};

export default withAppContext(HomePage);
